import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import cn from './index.module.css'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import { useMemo } from 'react'
import { declOfNum } from '@/helpers/declOfNum'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'

const SelectTariffValue = ({
  tariff,
  demo,
  value,
  setValue,
  onChangeDemo,
}: {
  tariff: ICourseTariff
  demo: boolean
  value: number
  setValue: (value: number) => void
  onChangeDemo: (value: boolean) => void
}) => {
  const getSliderLabel = (): string => {
    switch (tariff?.type) {
      case 'hour':
        return 'Выберите количество часов'
      case 'minute':
        return 'Выберите количество минут'
      case 'month':
        return 'Выберите количество месяцев'
      case 'stream_custom_period':
        return 'Выберите количество месяцев'
      case 'stream_custom_days':
        return 'Выберите количество дней'

      default:
        return ''
    }
  }

  const getSliderMax = (): number => {
    switch (tariff?.type) {
      case 'hour':
        return 24
      case 'minute':
        return 3600
      case 'month':
        return 24
      case 'stream_custom_period':
        return Math.max(
          ...(tariff?.tariffPeriods?.map((item) => item.months) ?? [0]),
        )
      case 'stream_custom_days':
        return Math.max(
          ...(tariff?.tariffDays?.map((item) => item.days) ?? [0]),
        )

      default:
        return 64
    }
  }

  const getSliderMarks = () => {
    if (tariff?.type === 'stream_custom_period') {
      return (
        tariff?.tariffPeriods?.reduce<Record<number, string>>((acc, item) => {
          acc[item.months] = item.months?.toString()
          return acc
        }, {}) ?? []
      )
    }
    if (tariff?.type === 'stream_custom_days') {
      return (
        tariff?.tariffDays?.reduce<Record<number, string>>((acc, item) => {
          acc[item.days] = item.days?.toString()
          return acc
        }, {}) ?? []
      )
    }

    return undefined
  }

  const getPeriodsMax = () => {
    if (tariff?.type === 'stream_custom_period') {
      return Math.max(
        ...(tariff?.tariffPeriods?.map((item) => item?.months) ?? [0]),
      )
    }
    if (tariff?.type === 'stream_custom_days') {
      return Math.max(...(tariff?.tariffDays?.map((item) => item?.days) ?? [0]))
    }

    return 0
  }

  const getPeriodsMin = () => {
    if (tariff?.type === 'stream_custom_period') {
      return Math.min(
        ...(tariff?.tariffPeriods?.map((item) => item.months) ?? [0]),
      )
    }
    if (tariff?.type === 'stream_custom_days') {
      return Math.min(...(tariff?.tariffDays?.map((item) => item?.days) ?? [0]))
    }

    return 0
  }

  const demoValue = useMemo(() => {
    return `${tariff?.demo_days ?? 0} ${declOfNum(tariff?.demo_days, [
      'день',
      'дня',
      'дней',
    ])} демо`
  }, [])

  const hasOnePeriod = () => {
    switch (tariff?.type) {
      case 'stream_custom_period':
        return tariff?.tariffPeriods?.length === 1
      case 'stream_custom_days':
        return tariff?.tariffDays?.length === 1

      default:
        return false
    }
  }

  const getOnePeriodValue = () => {
    switch (tariff?.type) {
      case 'stream_custom_period':
        return `${tariff?.tariffPeriods?.[0]?.months ?? 0} месяцев`
      case 'stream_custom_days':
        return `${tariff?.tariffDays?.[0]?.days ?? 0} дней`

      default:
        return false
    }
  }

  const getCheckboxValues = (): ISelect[] => {
    switch (tariff?.type) {
      case 'stream_custom_period':
        return (
          tariff?.tariffPeriods?.map((item) => {
            return {
              label: `${item?.name}, ${item?.months} ${declOfNum(item?.months, [
                'месяц',
                'месяца',
                'месяцев',
              ])}, ${item?.price} рублей`,
              value: item.months?.toString(),
            }
          }) ?? []
        )

      case 'stream_custom_days':
        return (
          tariff?.tariffDays?.map((item) => {
            return {
              label: `${item?.name}, ${item?.days} ${declOfNum(item?.days, [
                'день',
                'дня',
                'дней',
              ])}, ${item?.price} рублей`,
              value: item?.days?.toString(),
            }
          }) ?? []
        )
    }
    return []
  }

  const isChecked = (value: number, index: number): boolean => {
    switch (tariff?.type) {
      case 'stream_custom_period':
        return tariff?.tariffPeriods?.[index]?.months === value

      case 'stream_custom_days':
        return tariff?.tariffDays?.[index]?.days === value
    }
    return false
  }

  return (
    <div className={cn.wrapper}>
      {demo ? (
        <FieldWrapper label="Длительность">
          <div className={cn.demoField}>{demoValue}</div>
        </FieldWrapper>
      ) : (
        ['stream_custom_period', 'stream_custom_days'].includes(
          tariff?.type,
        ) && (
          <>
            {hasOnePeriod() ? (
              <FieldWrapper label="Длительность">
                <div className={cn.field}>{getOnePeriodValue()}</div>
              </FieldWrapper>
            ) : (
              <>
                {!!tariff?.isCheckboxesView ? (
                  <FieldWrapper label="Выберите период оплаты">
                    <div className={cn.checkboxList}>
                      {getCheckboxValues()?.map((item, index) => (
                        <CheckboxLabel
                          label={item?.label ?? ''}
                          key={index}
                          value={isChecked(value, index)}
                          onChange={() => setValue(Number(item?.value))}
                        />
                      ))}
                    </div>
                  </FieldWrapper>
                ) : (
                  <SliderField
                    label={getSliderLabel()}
                    value={value}
                    extraContent={value?.toString()}
                    step={null}
                    min={getPeriodsMin()}
                    max={getPeriodsMax()}
                    marks={getSliderMarks()}
                    onChange={setValue}
                  />
                )}
              </>
            )}
          </>
        )
      )}
      {['hour', 'minute', 'month'].includes(tariff?.type) && (
        <SliderField
          label={getSliderLabel()}
          value={value}
          extraContent={value?.toString()}
          min={getTariffValueMin(tariff)}
          max={getSliderMax()}
          onChange={setValue}
        />
      )}
      {tariff?.has_demo && (
        <CheckboxLabel
          label={'Активировать демо период'}
          value={demo}
          onChange={onChangeDemo}
        />
      )}
    </div>
  )
}

export const getTariffValueMin = (tariff: ICourseTariff): number => {
  switch (tariff?.type) {
    case 'stream_custom_period':
      return Math.min(
        ...(tariff?.tariffPeriods?.map((item) => item.months) ?? [0]),
      )
    case 'stream_custom_days':
      return Math.min(...(tariff?.tariffDays?.map((item) => item.days) ?? [0]))

    default:
      return 1
  }
}

export default SelectTariffValue
